// Contact Cards Manager Component
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Phone, Mail, MapPin, User, Building2, ChevronUp, ChevronDown, Share2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { ContactCardForm } from './AdminShared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARD_TYPES = [
  { value: 'person', label: 'Personne' },
  { value: 'address', label: 'Adresse' },
  { value: 'sales_point', label: 'Point de vente' },
  { value: 'secretary', label: 'Secrétariat' }
];

// Special ID for social links placeholder
const SOCIAL_LINKS_ID = '__social_links__';

const ContactInfoManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [socialLinksOrder, setSocialLinksOrder] = useState(999); // Default at the end
  const [hasSocialLinks, setHasSocialLinks] = useState(false);

  useEffect(() => { 
    fetchCards(); 
    fetchSocialLinksConfig();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact-cards`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCards(data || []);
      }
    } catch (err) {
      console.error('Error fetching contact cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialLinksConfig = async () => {
    try {
      // Check if there are active social links
      const socialResponse = await fetch(`${BACKEND_URL}/api/social-links`, { credentials: 'include' });
      if (socialResponse.ok) {
        const socialData = await socialResponse.json();
        const hasActive = Object.values(socialData).some(s => s?.is_active && s?.url);
        setHasSocialLinks(hasActive);
        
        // Get the order from social settings (stored in a special field)
        if (socialData._contact_order !== undefined) {
          setSocialLinksOrder(socialData._contact_order);
        }
      }
      
      // Also fetch from dedicated endpoint
      const orderResponse = await fetch(`${BACKEND_URL}/api/social-links-order`, { credentials: 'include' });
      if (orderResponse.ok) {
        const orderData = await orderResponse.json();
        if (orderData.contact_order !== undefined) {
          setSocialLinksOrder(orderData.contact_order);
        }
      }
    } catch (err) {
      console.error('Error fetching social links config:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchCards();
    } catch (err) {
      console.error('Error deleting card:', err);
    }
  };

  const handleSave = async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setEditingId(null);
      fetchCards();
    } catch (err) {
      console.error('Error saving card:', err);
    }
  };

  const handleAdd = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/contact-cards`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editForm)
      });
      setShowAddForm(false);
      setEditForm({});
      fetchCards();
    } catch (err) {
      console.error('Error adding card:', err);
    }
  };

  // Save social links order to backend
  const saveSocialLinksOrder = async (newOrder) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/social-links-order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ contact_order: newOrder })
      });
    } catch (err) {
      console.error('Error saving social links order:', err);
    }
  };

  const handleReorder = async (index, direction, itemId) => {
    // Get all items (cards + social links placeholder) sorted by order
    const allItems = getAllSortedItems();
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= allItems.length) return;
    
    const currentItem = allItems[index];
    const targetItem = allItems[targetIndex];
    
    // Swap orders
    const currentOrder = currentItem.order;
    const targetOrder = targetItem.order;
    
    // Update based on item type
    if (currentItem.id === SOCIAL_LINKS_ID) {
      // Moving social links
      setSocialLinksOrder(targetOrder);
      await saveSocialLinksOrder(targetOrder);
      
      // Update the target card
      if (targetItem.id !== SOCIAL_LINKS_ID) {
        const updatedCard = { ...targetItem, order: currentOrder };
        await fetch(`${BACKEND_URL}/api/admin/contact-cards/${targetItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(updatedCard)
        });
        fetchCards();
      }
    } else if (targetItem.id === SOCIAL_LINKS_ID) {
      // Swapping with social links
      setSocialLinksOrder(currentOrder);
      await saveSocialLinksOrder(currentOrder);
      
      // Update the current card
      const updatedCard = { ...currentItem, order: targetOrder };
      setCards(prev => prev.map(c => c.id === currentItem.id ? updatedCard : c));
      await fetch(`${BACKEND_URL}/api/admin/contact-cards/${currentItem.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(updatedCard)
      });
    } else {
      // Both are regular cards
      const updatedCurrent = { ...currentItem, order: targetOrder };
      const updatedTarget = { ...targetItem, order: currentOrder };
      
      setCards(prev => prev.map(c => {
        if (c.id === currentItem.id) return updatedCurrent;
        if (c.id === targetItem.id) return updatedTarget;
        return c;
      }));
      
      await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/contact-cards/${currentItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(updatedCurrent)
        }),
        fetch(`${BACKEND_URL}/api/admin/contact-cards/${targetItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify(updatedTarget)
        })
      ]);
    }
  };

  const getCardIcon = (cardType) => {
    switch(cardType) {
      case 'person': case 'secretary': return User;
      case 'address': return MapPin;
      case 'sales_point': return Building2;
      default: return User;
    }
  };

  // Combine cards with social links placeholder and sort
  const getAllSortedItems = () => {
    const items = [...cards];
    
    // Add social links placeholder
    items.push({
      id: SOCIAL_LINKS_ID,
      card_type: 'social_links',
      title_fr: 'Réseaux Sociaux',
      title_en: 'Social Networks',
      title_ar: 'شبكات التواصل',
      order: socialLinksOrder,
      is_special: true
    });
    
    return items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  const allSortedItems = getAllSortedItems();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Cartes de Contact</h2>
          <p className="text-sm text-slate-500 mt-1">Gérez les informations de contact affichées sur la page Contact</p>
        </div>
        <Button onClick={() => {
          setShowAddForm(true);
          setEditForm({ card_type: 'person', title_en: '', title_fr: '', title_ar: '', name: '', phones: [], emails: [], address: '', order: cards.length, is_active: true });
        }}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      {showAddForm && (
        <div className="border border-green-200 bg-green-50 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-slate-800 mb-4">Nouvelle Carte</h3>
          <ContactCardForm form={editForm} setForm={setEditForm} cardTypes={CARD_TYPES} onSave={handleAdd} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      <div className="space-y-4">
        {allSortedItems.map((item, index) => {
          // Special rendering for social links placeholder
          if (item.id === SOCIAL_LINKS_ID) {
            return (
              <div key={item.id} className="border-2 border-dashed border-blue-200 rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-start gap-4">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleReorder(index, 'up', item.id)}
                      disabled={index === 0}
                      className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-blue-400 hover:text-blue-600 hover:bg-blue-100'}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReorder(index, 'down', item.id)}
                      disabled={index === allSortedItems.length - 1}
                      className={`p-1 rounded ${index === allSortedItems.length - 1 ? 'text-slate-200' : 'text-blue-400 hover:text-blue-600 hover:bg-blue-100'}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Share2 className="text-blue-500" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">Réseaux Sociaux</span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs flex items-center gap-1">
                        <Lock size={10} /> Lecture seule
                      </span>
                    </div>
                    <p className="text-sm text-blue-600">
                      {hasSocialLinks 
                        ? "Ce bloc affiche vos réseaux sociaux configurés dans « Apparence → Réseaux Sociaux »"
                        : "Aucun réseau social actif. Configurez-les dans « Apparence → Réseaux Sociaux »"
                      }
                    </p>
                    <p className="text-xs text-blue-400 mt-1">
                      Utilisez les flèches pour positionner ce bloc parmi les cartes de contact
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          
          // Regular card rendering
          const Icon = getCardIcon(item.card_type);
          return (
            <div key={item.id} className="border border-slate-200 rounded-lg p-4 bg-white">
              {editingId === item.id ? (
                <ContactCardForm form={editForm} setForm={setEditForm} cardTypes={CARD_TYPES} onSave={() => handleSave(item.id)} onCancel={() => setEditingId(null)} />
              ) : (
                <div className="flex items-start gap-4">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleReorder(index, 'up', item.id)}
                      disabled={index === 0}
                      className={`p-1 rounded ${index === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReorder(index, 'down', item.id)}
                      disabled={index === allSortedItems.length - 1}
                      className={`p-1 rounded ${index === allSortedItems.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-slate-400" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{CARD_TYPES.find(t => t.value === item.card_type)?.label}</span>
                      {!item.is_active && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">Inactif</span>}
                    </div>
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{item.title_fr}</p>
                    {item.name && <p className="font-medium text-slate-800">{item.name}</p>}
                    {item.phones && item.phones.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {item.phones.map((phone, idx) => <span key={idx} className="text-sm text-slate-500 flex items-center gap-1"><Phone size={12} /> {phone}</span>)}
                      </div>
                    )}
                    {item.emails && item.emails.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {item.emails.map((email, idx) => <span key={idx} className="text-sm text-slate-500 flex items-center gap-1"><Mail size={12} /> {email}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingId(item.id); setEditForm(item); }}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {cards.length === 0 && !showAddForm && <div className="text-center py-12 text-slate-500">Aucune carte de contact.</div>}
    </div>
  );
};

export default ContactInfoManager;
